import { ApplicationRef, ComponentFactoryResolver, Injectable, Injector } from '@angular/core';

import { DomPortalHost } from '../portal/dom-portal-host';
import { OverlayContainer } from './overlay-container';
import { OverlayRef } from './overlay-ref';
import { OverlayState } from './overlay-state';
import { OverlayPositionBuilder } from './position/overlay-position-builder';

/** Next overlay unique ID. */
let nextUniqueId = 0;

/**
 * Service to create Overlays. Overlays are dynamically added pieces of floating UI, meant to be
 * used as a low-level building building block for other components. Dialogs, tooltips, menus,
 * selects, etc. can all be built using overlays. The service should primarily be used by authors
 * of re-usable components rather than developers building end-user applications.
 *
 * An overlay *is* a PortalHost, so any kind of Portal can be loaded into one.
 */
@Injectable()
export class OverlayService {
  constructor(
    private _overlayContainer: OverlayContainer,
    private _componentFactoryResolver: ComponentFactoryResolver,
    private _positionBuilder: OverlayPositionBuilder,
    private _appRef: ApplicationRef,
    private _injector: Injector,
  ) {}

  /**
   * Creates an overlay.
   * @param state State to apply to the overlay.
   * @returns Reference to the created overlay.
   */
  public create(state: OverlayState): OverlayRef {
    return this.createOverlayRef(this._createPaneElement(state.level), state);
  }

  /**
   * Returns a position builder that can be used, via fluent API,
   * to construct and configure a position strategy.
   */
  public position(): OverlayPositionBuilder {
    return this._positionBuilder;
  }

  /**
   * Creates the DOM element for an overlay and appends it to the overlay container.
   * @returns Newly-created pane element
   */
  private _createPaneElement(overlayLevel: number): HTMLElement {
    const pane = document.createElement('div');
    pane.id = `rt-overlay-${nextUniqueId++}`;
    pane.classList.add('rt-overlay-pane');
    pane.dataset.overlayLevel = `${overlayLevel}`;

    this._overlayContainer.insert(pane, overlayLevel);

    return pane;
  }

  /**
   * Create a DomPortalHost into which the overlay content can be loaded.
   * @param pane The DOM element to turn into a portal host.
   * @returns A portal host for the given DOM element.
   */
  private _createPortalHost(pane: HTMLElement): DomPortalHost {
    return new DomPortalHost(pane, this._componentFactoryResolver, this._appRef, this._injector);
  }

  /**
   * Creates an OverlayRef for an overlay in the given DOM element.
   * @param pane DOM element for the overlay
   * @param state
   */
  private createOverlayRef(pane: HTMLElement, state: OverlayState): OverlayRef {
    return new OverlayRef(this._createPortalHost(pane), pane, state);
  }
}
